import { pageLoaderController } from "../../context/PageLoaderContext";
import { devLog, devWarn } from "../../utils/devLogger";
import { flutterBridge } from "../../utils/flutterBridge";
import { toast } from "react-toastify";

// 전역 토큰 갱신 상태 관리 (context 외부에서도 사용 가능)
let globalTokenRefreshingState = false;
const tokenRefreshListeners: ((isRefreshing: boolean) => void)[] = [];

export const subscribeToTokenRefreshState = (callback: (isRefreshing: boolean) => void) => {
  tokenRefreshListeners.push(callback);
  return () => {
    const index = tokenRefreshListeners.indexOf(callback);
    if (index > -1) {
      tokenRefreshListeners.splice(index, 1);
    }
  };
};

const updateGlobalTokenRefreshState = (isRefreshing: boolean) => {
  globalTokenRefreshingState = isRefreshing;
  tokenRefreshListeners.forEach(callback => callback(isRefreshing));
};

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface CallApiParams {
  title: string;
  url: string;
  method?: HttpMethod;
  body?: Record<string, any>;
  params?: Record<string, any>; // 쿼리 파라미터
  isCallPageLoader?: boolean;
  accessToken?: string;
  headers?: Record<string, string>; // 추가 헤더
  useDebounce?: boolean; // 디바운스 적용 여부
  debounceDelay?: number; // 디바운스 지연 시간 (ms)
}

// 진행 중인 API 요청을 추적하기 위한 Map
const pendingRequests = new Map<string, Promise<any>>();

// 토큰 갱신 중인지 추적하는 플래그
let isRefreshing = false;
// 토큰 갱신 대기 중인 요청들
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

// 토큰 만료 메시지 국제화
const getTokenExpiredMessage = (): string => {
  // localStorage에서 언어 설정 확인
  const savedLang = localStorage.getItem('appLang');
  const lang = savedLang || (navigator.language.startsWith('ko') ? 'ko' : 'en');
  
  return lang === 'ko' 
    ? '로그인이 만료되었습니다. 다시 로그인해주세요.'
    : 'Your login has expired. Please log in again.';
};

// 토큰 갱신 처리
async function handleTokenRefresh(): Promise<string> {
  // 직접 Flutter에서 받은 refresh token 사용 (localStorage 확인 불필요)
  const flutterRefreshToken = flutterBridge.getReceivedRefreshToken();
  
  if (!flutterRefreshToken) {
    throw new Error('Refresh token not available from Flutter');
  }
  
  try {
    // 환경에 따른 API 호스트 결정
    let apiHost = process.env.REACT_APP_API_HOST;
    if (typeof window !== 'undefined') {
      const isHttps = window.location.protocol === 'https:';
      if (!isHttps) {
        apiHost = process.env.REACT_APP_API_DEV_URL;
      } else {
        apiHost = process.env.REACT_APP_API_PROD_URL;
      }
    }
    
    const refreshUrl = `${apiHost}/api/users/refresh`;
    
    const refreshResponse = await fetch(refreshUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${flutterRefreshToken}`, // refresh token을 Bearer로 전송
      },
    });
    
    const refreshData = await refreshResponse.json();
    
    if (refreshResponse.ok && refreshData.statusCode === 200) {
      // 헤더에서 access token 추출 (서버가 헤더로 토큰을 전송)
      const accessToken = refreshResponse.headers.get('access-token') || 
                         refreshResponse.headers.get('access_token') || '';
      
      if (accessToken) {
        // 새 access token만 localStorage에 저장 (refresh token은 저장하지 않음)
        localStorage.setItem('accessToken', accessToken);
        
        // 새로운 refresh token이 헤더에 있다면 Flutter에 전달
        const newRefreshToken = refreshResponse.headers.get('refresh-token') || 
                               refreshResponse.headers.get('refresh_token') || '';
        if (newRefreshToken) {
          flutterBridge.handleRefreshTokenFromFlutter(newRefreshToken, accessToken);
        }
        
        devLog('🔄 토큰 갱신 성공 (헤더에서 추출):', { 
          accessToken: accessToken.substring(0, 20) + '...',
          refreshToken: newRefreshToken ? newRefreshToken.substring(0, 20) + '...' : '없음'
        });
        
        return accessToken;
      } else {
        throw new Error('Access token not found in response headers');
      }
    } else {
      throw new Error('Token refresh failed');
    }
  } catch (error) {
    devWarn('❌ 토큰 갱신 실패:', error);
    
    // 토큰 갱신 실패 시 access token만 제거 (refresh token은 Flutter가 관리)
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    
    // 로그인 페이지로 리다이렉트
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    
    throw error;
  }
}

export async function callApi<T = any>({
  title,
  url,
  method = 'GET',
  body,
  params,
  isCallPageLoader = false,
  accessToken,
  headers: customHeaders = {},
  useDebounce = false,
  debounceDelay = 300,
}: CallApiParams): Promise<{ data: T; headers?: Headers; status: number; ok: boolean }> {
  // 디바운스 로직: 동일한 요청이 진행 중인지 확인
  if (useDebounce) {
    // 요청 식별자 생성 (URL + method + body + params 조합)
    const requestKey = JSON.stringify({ url, method, body, params });
    
    // 이미 진행 중인 동일한 요청이 있다면 그 결과를 반환
    if (pendingRequests.has(requestKey)) {
      devLog(`🔄 [${title}] 디바운스: 동일한 요청이 진행 중입니다.`);
      return pendingRequests.get(requestKey)!;
    }
    
    // 새로운 요청 Promise 생성 및 등록
    const requestPromise = executeApiRequest();
    pendingRequests.set(requestKey, requestPromise);
    
    try {
      const result = await requestPromise;
      return result;
    } finally {
      // 요청 완료 후 pendingRequests에서 제거
      pendingRequests.delete(requestKey);
    }
  }
  
  // 디바운스를 사용하지 않는 경우 바로 실행
  return executeApiRequest();
  
  // API 요청 실행 함수
  async function executeApiRequest(): Promise<{ data: T; headers?: Headers; status: number; ok: boolean }> {
    // 환경에 따른 API URL 분기 처리
    let apiUrl = url;
    if (typeof window !== 'undefined') {
      // 상대경로인 경우에만 API 호스트 붙이기
      if (url.startsWith('/')) {
        const apiHost = process.env.REACT_APP_API_HOST;
        
        devLog(`🔍 [callApi] API_HOST: ${apiHost}`);
        
        if (!apiHost) {
          devWarn(`❌ REACT_APP_API_HOST 환경변수가 설정되지 않았습니다.`);
          throw new Error('API 호스트가 설정되지 않았습니다.');
        }
        
        apiUrl = `${apiHost}${url}`;
      }
    }

    // 쿼리 파라미터 처리
    if (params && Object.keys(params).length > 0) {
      const urlObj = new URL(apiUrl);
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          urlObj.searchParams.append(key, String(value));
        }
      });
      apiUrl = urlObj.toString();
    }

    devLog(`🌐 [${title}] ${method} ${typeof window !== 'undefined' ? `(${window.location.protocol})` : ''}`, apiUrl);
    if (body) {
      devLog(`📝 [${title}] Body:`, body);
    }
    if (params) {
      devLog(`🔍 [${title}] Params:`, params);
    }
    
    if (isCallPageLoader) pageLoaderController.open();

    let returnValue = '';

    try {
      const headers: Record<string, string> = {
        ...customHeaders,
      };

      // GET 요청이 아닌 경우에만 Content-Type 추가
      if (method !== 'GET' && body) {
        headers['Content-Type'] = 'application/json';
      }

      // accessToken이 있으면 Authorization 헤더 추가
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const fetchOptions: RequestInit = {
        method,
        headers,
      };

      // GET 요청이 아니고 body가 있는 경우에만 body 추가
      if (method !== 'GET' && body) {
        fetchOptions.body = JSON.stringify(body);
      }

      const response = await fetch(apiUrl, fetchOptions);

      returnValue = await response.text();
      devLog(`📱 [${title}] 응답 (${response.status})`, returnValue);
      
      // 응답 헤더도 함께 반환
      const responseHeaders = response.headers;

      // HTTP 에러 상태 코드 처리
      if (!response.ok) {
        let errorData;
        try {
          errorData = JSON.parse(returnValue);
        } catch (e) {
          errorData = { message: returnValue || 'Unknown error' };
        }
        
        // 401 에러 (토큰 만료) 처리
        if (response.status === 401 && accessToken) {
          devLog('🔄 토큰 만료 감지...');
          
          // refresh token이 있는지 확인 (Flutter에서 받은 토큰)
          const refreshToken = flutterBridge.getReceivedRefreshToken();
          
          if (refreshToken) {
            devLog('� Refresh token 발견 - 자동 갱신 시도...');
            
            if (isRefreshing) {
              // 이미 토큰 갱신 중이면 대기열에 추가
              return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
              }).then((newToken) => {
                // 새 토큰으로 원래 요청 재시도
                return executeApiRequest();
              });
            }
            
            isRefreshing = true;
            updateGlobalTokenRefreshState(true); // 전역 상태 업데이트
            
            try {
              const newAccessToken = await handleTokenRefresh();
              
              // 대기 중인 요청들에게 새 토큰 전달
              failedQueue.forEach(({ resolve }) => resolve(newAccessToken));
              failedQueue = [];
              
              isRefreshing = false;
              updateGlobalTokenRefreshState(false); // 전역 상태 업데이트
              
              // 새 토큰으로 원래 요청 재시도
              accessToken = newAccessToken;
              return executeApiRequest();
              
            } catch (refreshError) {
              // 토큰 갱신 실패 시 대기 중인 요청들 모두 실패 처리
              failedQueue.forEach(({ reject }) => reject(refreshError));
              failedQueue = [];
              isRefreshing = false;
              updateGlobalTokenRefreshState(false); // 전역 상태 업데이트
              
              // 토큰 갱신 실패 시에만 로그인 페이지로 리다이렉트
              devLog('❌ 토큰 갱신 실패 - 로그인 페이지로 리다이렉트');
              
              // 토큰 정리
              localStorage.removeItem('accessToken');
              localStorage.removeItem('user');
              
              // 로그인 페이지로 리다이렉트
              if (typeof window !== 'undefined') {
                toast.error(getTokenExpiredMessage());
                window.location.href = '/login';
              }
              
              throw refreshError;
            }
          } else {
            // Refresh token이 없는 경우 - 로그아웃 처리 후 로그인 페이지로 리다이렉트
            devLog('❌ Refresh token 없음 - 로그인 페이지로 리다이렉트');
            
            // 토큰 정리
            localStorage.removeItem('accessToken');
            localStorage.removeItem('user');
            
            // 로그인 페이지로 리다이렉트
            if (typeof window !== 'undefined') {
              toast.error(getTokenExpiredMessage());
              window.location.href = '/login';
            }
            
            // 401 에러를 그대로 반환
            return { 
              data: errorData, 
              headers: responseHeaders, 
              status: response.status, 
              ok: response.ok 
            };
          }
        }
        
        // 500번대 에러만 throw, 나머지는 정상 응답으로 처리
        if (response.status >= 500) {
          const error = new Error(errorData.message || `HTTP ${response.status} Error`);
          (error as any).status = response.status;
          (error as any).data = errorData;
          throw error;
        }
        
        // 400번대 에러는 정상 응답으로 반환 (4xx 클라이언트 에러)
        return { 
          data: errorData, 
          headers: responseHeaders, 
          status: response.status, 
          ok: response.ok 
        };
      }
      
      try {
        const parsedData = JSON.parse(returnValue);
        return { 
          data: parsedData, 
          headers: responseHeaders, 
          status: response.status, 
          ok: response.ok 
        };
      } catch (e) {
        devWarn(`⚠️ [${title}] JSON 파싱 실패`, e);
        
        // HTML 응답인지 확인
        if (returnValue.trim().startsWith('<!DOCTYPE') || returnValue.trim().startsWith('<html')) {
          const error = new Error(`잘못된 API 엔드포인트: HTML 페이지가 반환되었습니다.`);
          (error as any).status = response.status;
          (error as any).data = {
            statusCode: response.status,
            message: "잘못된 API 엔드포인트",
            error: {
              customMessage: "API 엔드포인트가 올바르지 않거나 서버에서 HTML 페이지를 반환했습니다."
            }
          };
          throw error;
        }
        
        return { 
          data: returnValue as T, 
          headers: responseHeaders, 
          status: response.status, 
          ok: response.ok 
        };
      }
    } catch (error) {
      devLog(`❌ [${title}] API 요청 에러`, error);
      throw error; // 에러를 다시 throw해서 호출부에서 처리할 수 있도록
    } finally {
      if (isCallPageLoader) pageLoaderController.close();
    }
  }
}

// 편의 함수들
export const callApiGet = <T = any>(params: Omit<CallApiParams, 'method'>) => 
  callApi<T>({ ...params, method: 'GET' });

export const callApiPost = <T = any>(params: Omit<CallApiParams, 'method'>) => 
  callApi<T>({ ...params, method: 'POST' });

export const callApiPut = <T = any>(params: Omit<CallApiParams, 'method'>) => 
  callApi<T>({ ...params, method: 'PUT' });

export const callApiDelete = <T = any>(params: Omit<CallApiParams, 'method'>) => 
  callApi<T>({ ...params, method: 'DELETE' });

export const callApiPatch = <T = any>(params: Omit<CallApiParams, 'method'>) => 
  callApi<T>({ ...params, method: 'PATCH' });
